import { MoreHorizontal, Send, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useCancelInvitation } from '@/api/organizations/queries';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Invitation } from '@/lib/auth-client';
import { getRoleBadgeVariant } from './utils';

interface PendingInvitationsTableProps {
  invitations: Invitation[];
}

export function PendingInvitationsTable({ invitations }: PendingInvitationsTableProps) {
  const cancelInvitationMutation = useCancelInvitation();
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredInvitations = useMemo(() => {
    if (statusFilter === 'all') {
      return invitations;
    }
    return invitations.filter((invitation) => invitation.status === statusFilter);
  }, [invitations, statusFilter]);

  const availableStatuses = useMemo(() => {
    const statuses = [...new Set(invitations.map((inv) => inv.status))];
    return statuses.sort();
  }, [invitations]);

  const handleCancelInvitation = async (invitationId: string, email: string) => {
    try {
      await cancelInvitationMutation.mutateAsync({
        invitationId,
      });

      toast.success(`Invitation to ${email} has been canceled`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to cancel invitation');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1.5">
            <CardTitle>
              Invitations{' '}
              <span className="text-muted-foreground">{filteredInvitations.length || 0}</span>
            </CardTitle>
            <CardDescription>Users who have been invited but haven't joined yet</CardDescription>
          </div>
          {availableStatuses.length > 0 && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({invitations.length})</SelectItem>
                {availableStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)} (
                    {invitations.filter((inv) => inv.status === status).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent className="mx-[-16px] px-4">
        {filteredInvitations.length === 0 ? (
          <div className="text-center py-8">
            <Send className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {statusFilter === 'all' ? 'No invitations' : `No ${statusFilter} invitations`}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {statusFilter === 'all'
                ? 'All invitations have been accepted or there are no pending invites.'
                : `No invitations with ${statusFilter} status found.`}
            </p>
          </div>
        ) : (
          <Table containerClassName="px-4">
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {invitation.email.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{invitation.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getRoleBadgeVariant(invitation.role)}>{invitation.role}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={invitation.status === 'pending' ? 'secondary' : 'destructive'}>
                      {invitation.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(invitation.expiresAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {invitation.status === 'pending' && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleCancelInvitation(invitation.id, invitation.email)}
                            className="text-destructive"
                          >
                            <X className="h-4 w-4" />
                            Cancel Invitation
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
